import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
  ApplePay,
  isThirdPartyApplePay,
} from "../modules/express-checkout/wallets/apple-pay";
import { getMode } from "../modules/express-checkout";

function resolveApplePayButtonType(mode) {
  if (mode === 'autopay') return 'subscribe';
  if (mode === 'tokenization') return 'plain';
  return 'pay';
}

export const ApplePayButton = ({
  config = {},
  vTerminal,
  domain,
  paypoint,
  merchantIdentifier,
}) => {
  const { expressCheckout = {} } = config || {};
  const { applePay = {}, appearance = {} } = expressCheckout;
  const refButton = useRef();
  // Guards the third-party (QR) flow against a second click stacking a duplicate
  // session while the QR is open. Reset only when the flow settles (onSettled),
  // since a QR stays open far longer than any fixed debounce. Safari's flow is a
  // modal OS sheet, so it can't be double-triggered and needs no guard.
  const remoteInFlightRef = useRef(false);

  useEffect(() => {
    const button = refButton.current;
    if (!button) return;

    const handleClick = () => {
      if (!domain) return;
      const payabliConfig = {
        ...config,
        vTerminal,
        applePayMerchantId: merchantIdentifier,
      };
      const wallet = new ApplePay(payabliConfig, paypoint);
      // Third-party browsers (Chrome/Edge/Firefox) draw the Apple Pay QR in
      // the DOM of whoever runs the SDK; from this iframe it would be trapped.
      // Delegate to the top-level host so the QR renders full-screen on the
      // merchant page. Safari keeps the native in-iframe flow (its OS sheet
      // already escapes the iframe).
      if (isThirdPartyApplePay()) {
        if (remoteInFlightRef.current) return;
        remoteInFlightRef.current = true;
        wallet.applePayInitRemote(domain, () => {
          remoteInFlightRef.current = false;
        });
      } else {
        wallet.applePayInit(domain);
      }
      // The button keeps focus after the click; drop it so no focus ring
      // lingers on the button while the Apple Pay sheet/QR takes over.
      if (typeof button.blur === "function") {
        button.blur();
      }
    };

    button.addEventListener("click", handleClick);
    return () => button.removeEventListener("click", handleClick);
  }, [config, vTerminal, domain, paypoint, merchantIdentifier]);

  return (
    <>
      <Helmet>
        <style type="text/css">{`
            apple-pay-button#btn-apple-pay-${config.randomId} {
                --apple-pay-button-width: 100%;
                --apple-pay-button-height: ${appearance.buttonHeight || 44}px;
                --apple-pay-button-border-radius: ${
                  appearance.buttonBorderRadius || 4
                }px;
                --apple-pay-button-padding: ${appearance.padding?.x || 8}px ${
          appearance.padding?.y || 11
        }px;
                --apple-pay-button-box-sizing: border-box;
            }
         `}</style>
      </Helmet>
      <apple-pay-button
        id={`btn-apple-pay-${config.randomId}`}
        ref={refButton}
        buttonstyle={applePay.buttonStyle || "black"}
        type={applePay.buttonType || resolveApplePayButtonType(getMode(config))}
        locale={applePay.language || "en-US"}
      />
    </>
  );
};
